/**
 * Slice 13 — Cleaner job identification.
 * Short street + unit/floor for My jobs cards (never city / province / postal).
 */

const STREET_ABBREV: Array<[RegExp, string]> = [
  [/\bCrescent\b/gi, "Cres"],
  [/\bAvenue\b/gi, "Ave"],
  [/\bBoulevard\b/gi, "Blvd"],
  [/\bDrive\b/gi, "Dr"],
  [/\bStreet\b/gi, "St"],
  [/\bRoad\b/gi, "Rd"],
  [/\bPlace\b/gi, "Pl"],
  [/\bCourt\b/gi, "Ct"],
  [/\bLane\b/gi, "Ln"],
  [/\bTerrace\b/gi, "Ter"],
  [/\bHighway\b/gi, "Hwy"],
];

function abbreviateStreetTypes(s: string): string {
  let out = s;
  for (const [re, abbr] of STREET_ABBREV) out = out.replace(re, abbr);
  return out;
}

/** Strip trailing city / province / postal from a freeform address line. */
function streetOnly(raw: string): string {
  let s = raw.trim();
  if (!s) return "";
  // Prefer first comma segment when the rest looks like city/region.
  const comma = s.indexOf(",");
  if (comma > 0) {
    const head = s.slice(0, comma).trim();
    const tail = s.slice(comma + 1).trim();
    if (/[A-Za-z]/.test(tail) && /\d/.test(head)) s = head;
  }
  // Drop trailing postal / province tokens if still present.
  s = s
    .replace(/\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/gi, "")
    .replace(/\b(ON|QC|BC|AB|MB|SK|NS|NB|NL|PE|YT|NT|NU|CA|USA|US)\b\.?/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/[,\s]+$/g, "")
    .trim();
  return abbreviateStreetTypes(s);
}

/**
 * Format unit / floor / building cue for the address middot tail.
 * Examples: 606 → Unit 606 · Floor 2 → 2nd floor · Basement → Basement · PH3 → PH3
 */
export function formatUnitCue(unit?: string | null, unitLocation?: string | null): string | null {
  const raw = (unit || unitLocation || "").trim();
  if (!raw) return null;

  const floorOnly = raw.match(/^(?:floor|fl\.?)\s*(\d+)$/i);
  if (floorOnly) {
    const n = Number(floorOnly[1]);
    const ord =
      n % 100 >= 11 && n % 100 <= 13
        ? `${n}th`
        : n % 10 === 1
          ? `${n}st`
          : n % 10 === 2
            ? `${n}nd`
            : n % 10 === 3
              ? `${n}rd`
              : `${n}th`;
    return `${ord} floor`;
  }

  if (/^(basement|bSMT|bsmt|ground|lobby|main|penthouse)$/i.test(raw)) {
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  }
  if (/^ph\b/i.test(raw) || /^tower\b/i.test(raw) || /\btower\b/i.test(raw)) {
    return raw.replace(/\s+/g, " ").trim();
  }
  if (/^\d+[A-Za-z]?$/.test(raw) || /^unit\s+/i.test(raw) || /^#/.test(raw)) {
    const num = raw.replace(/^unit\s+/i, "").replace(/^#/, "").trim();
    return `Unit ${num}`;
  }
  // Already descriptive (e.g. "2nd floor", "PH3 Tower B")
  if (/\bfloor\b/i.test(raw) || /\bunit\b/i.test(raw)) return raw.replace(/\s+/g, " ").trim();
  return raw.replace(/\s+/g, " ").trim();
}

/** Leading street number for missing-photo tile (e.g. "210"). */
export function streetNumberFromAddress(address?: string | null): string | null {
  const street = streetOnly(address || "");
  const m = street.match(/^(\d+[A-Za-z]?)\b/);
  return m?.[1] ?? null;
}

export type ShortAddressLines = {
  /** Bold line 1 — street · unit cue, or listing name when no address */
  line1: string;
  /** Grey line 2 — listing · window, or window only when line1 is the listing name */
  line2: string | null;
  streetNumber: string | null;
  hasAddress: boolean;
};

/**
 * Build Slice 13 identification lines for a cleaner job card.
 */
export function formatShortAddress(opts: {
  address?: string | null;
  formattedAddress?: string | null;
  unit?: string | null;
  unitLocation?: string | null;
  propertyName?: string | null;
  windowLabel?: string | null;
}): ShortAddressLines {
  const name = (opts.propertyName || "").trim() || "Property";
  const window = (opts.windowLabel || "").trim() || null;
  const street = streetOnly(opts.address || opts.formattedAddress || "");
  const cue = formatUnitCue(opts.unit, opts.unitLocation);
  const streetNumber = streetNumberFromAddress(street || opts.address || opts.formattedAddress);

  if (street) {
    const line1 = cue ? `${street} · ${cue}` : street;
    const line2 = window ? `${name} · ${window}` : name;
    return { line1, line2, streetNumber, hasAddress: true };
  }

  // No address on file — listing name takes the bold slot; window alone on line 2.
  return {
    line1: name,
    line2: window,
    streetNumber: null,
    hasAddress: false,
  };
}
